import React, { useState } from 'react';
import { Handle, Position, useReactFlow, Edge } from 'reactflow';
import { getColoredIcon } from '../shared/functions';
import DroppableZone from './DroppableZone';
import UploadButton from '../uploadButton/UploadButton';
import FileList from './FileList';

interface CustomNodeProps {
    id: string;
    data: any;
}

export default function CustomNode({ id, data }: CustomNodeProps) {
    const [hovered, setHovered] = useState(false);
    const [files, setFiles] = useState<File[]>(data.files || []);
    const reactFlowInstance = useReactFlow();

    const handleFileSelect = (selectedFiles: File[]) => {
        const newFiles = selectedFiles.slice(0, 1);
        setFiles(newFiles);
        updateNodeData(newFiles);
    };

    const handleDeleteFile = (index: number) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        updateNodeData(newFiles);
    };

    const updateNodeData = (newFiles: File[]) => {
        const updatedData = {
            ...data,
            files: newFiles,
            hasFilesChanged: newFiles.length > 0
        };

        const event = new CustomEvent('nodeDataChanged', {
            detail: {
                nodeId: id,
                data: updatedData
            },
            bubbles: true,
            composed: true
        });

        const nodeElement = document.querySelector(`[data-node-id="${id}"]`);
        if (nodeElement) {
            nodeElement.dispatchEvent(event);
        }
    };

    const incomingEdges = reactFlowInstance.getEdges().filter((edge: Edge) => edge.target === id);
    const sourceNodesInfo = incomingEdges.map((edge: Edge) => {
        const sourceNode = reactFlowInstance.getNode(edge.source);
        return sourceNode?.data?.body || `Node ${edge.source}`;
    });

    const tooltipTitle = sourceNodesInfo.length > 0
        ? `Conectado de: ${sourceNodesInfo.join(', ')}`
        : 'Nenhuma conexão de entrada';
    return (
        <div className='custom-status' id={data?.nodeId || ''}>
            <div className='custom-node'
                id={data?.nodeId || ''}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    opacity: hovered ? 1 : '',
                }}>
                <div className='header-node'
                    style={{
                        background: data.bg,
                        color: data.color || '#4d575e',
                    }}>
                    {(data.hasFilesChanged) && (
                        <span className="dot-not-save" title="Alteração não salva"></span>
                    )}
                    <div className="header-content">
                        <span>{data.title}</span>
                        {data.hasDroppableZone && files.length === 0 && (
                            <UploadButton onFileSelect={handleFileSelect} multiple={false} />
                        )}
                    </div>
                </div>
                <div className='body-node'>
                    <span className='icon-body' dangerouslySetInnerHTML={{ __html: getColoredIcon(data) }} />
                    <span className='body-node-content'>
                        {data.body}
                        {data.excelTitle && (
                            <span className='excel-title'>{data.excelTitle}</span>
                        )}
                    </span>
                </div>
                {data.hasDroppableZone && (
                    <div className='footer-node'>
                        <hr className='footer-node-separator'></hr>
                        <p className="files-list-title">
                            Arquivos <span className='files-list-title-count'>({files.length})</span>
                        </p>
                        <div className='footer-node-content'>
                            <FileList files={files} onDeleteFile={handleDeleteFile} />

                            {files.length === 0 && (
                                <DroppableZone id={id} data={data} onFilesUpdate={handleFileSelect} />
                            )}
                        </div>
                    </div>
                )}
                {data?.subFlowCount && (data?.subFlowCount?.success > 0 || data?.subFlowCount?.errors > 0 || data?.subFlowCount?.pending > 0) && (
                    <div className='footer-node'>
                        {data?.status === 'error' && (
                            <div className='quantity-errors-container' title="Arquivos com erro">
                                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g id="Icon / Circle Cancel" clipPath="url(#clip0_13018_96083)">
                                        <path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M10 0C4.5 0 0 4.5 0 10C0 15.5 4.5 20 10 20C15.5 20 20 15.5 20 10C20 4.5 15.5 0 10 0ZM10 18C5.6 18 2 14.4 2 10C2 5.6 5.6 2 10 2C14.4 2 18 5.6 18 10C18 14.4 14.4 18 10 18ZM13.7 6.3C13.3 5.9 12.7 5.9 12.3 6.3L10 8.6L7.7 6.3C7.3 5.9 6.7 5.9 6.3 6.3C5.9 6.7 5.9 7.3 6.3 7.7L8.6 10L6.3 12.3C5.9 12.7 5.9 13.3 6.3 13.7C6.5 13.9 6.7 14 7 14C7.3 14 7.5 13.9 7.7 13.7L10 11.4L12.3 13.7C12.5 13.9 12.8 14 13 14C13.2 14 13.5 13.9 13.7 13.7C14.1 13.3 14.1 12.7 13.7 12.3L11.4 10L13.7 7.7C14.1 7.3 14.1 6.7 13.7 6.3Z" fill="#d23c3c" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_13018_96083">
                                            <rect width="20" height="20" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                {data?.subFlowCount?.errors >= 1 && (
                                    <span className="quantity-errors">
                                        {data?.subFlowCount?.errors}/{data?.subFlowCount?.total}
                                    </span>
                                )}
                            </div>
                        )}
                        {data?.status === 'success' && (
                            <div className='quantity-success-container' title="Arquivos com sucesso">
                                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 14.4C4.48 14.4 1.6 11.52 1.6 8C1.6 4.48 4.48 1.6 8 1.6C11.52 1.6 14.4 4.48 14.4 8C14.4 11.52 11.52 14.4 8 14.4ZM9.84 5.84L7.2 8.48L6.16 7.44C5.84 7.12 5.36 7.12 5.04 7.44C4.72 7.76 4.72 8.24 5.04 8.56L6.64 10.16C6.8 10.32 6.96 10.4 7.2 10.4C7.44 10.4 7.6 10.32 7.76 10.16L10.96 6.96C11.28 6.64 11.28 6.16 10.96 5.84C10.64 5.52 10.16 5.52 9.84 5.84Z" fill="#1CA058" />
                                </svg>
                                {data?.subFlowCount?.success >= 1 && (
                                    <span className="quantity-success">
                                        {data?.subFlowCount?.success}/{data?.subFlowCount?.total}
                                    </span>
                                )}
                            </div>
                        )}
                        {data?.status === 'ongoing' && (
                            <div className='quantity-pending-container' title="Arquivos pendentes">
                                <span className='loadNode'></span>
                                {data?.subFlowCount?.pending >= 0 && (
                                    <span className="quantity-pending">
                                        {(data?.subFlowCount?.success + data?.subFlowCount?.errors)}/{data?.subFlowCount?.total}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <Handle type='target' position={Position.Left} title={tooltipTitle} style={{ visibility: 'visible', cursor: 'auto' }} />
                <Handle type='source' position={Position.Right} style={{ visibility: 'hidden' }} />
            </div>
        </div>
    );
}

